import React from "react";
import { Link } from "react-router-dom";
import WebHeader from "./WebHeader";
import WebFooter from "./WebFooter";

const values = [
  { icon: "🎯", title: "Precision", desc: "AI that understands your skills deeply, not just keywords on a page." },
  { icon: "⚖️", title: "Fairness", desc: "Unbiased screening that gives every candidate an equal opportunity." },
  { icon: "🔒", title: "Trust", desc: "Verified employers, transparent processes, and data you control." },
  { icon: "🚀", title: "Speed", desc: "From application to shortlist in minutes, not weeks." },
];

const team = [
  { name: "Arjun Nair", role: "CEO & Co-Founder", emoji: "👨‍💼" },
  { name: "Priya Menon", role: "Head of AI & ML", emoji: "👩‍💻" },
  { name: "Rahul Sharma", role: "Product Lead", emoji: "👨‍🔬" },
  { name: "Divya Krishnan", role: "Head of Talent", emoji: "👩‍🎓" },
];

const milestones = [
  { year: "2021", event: "Platform founded with a vision to fix broken hiring" },
  { year: "2022", event: "Launched AI resume parser serving 10,000+ candidates" },
  { year: "2023", event: "Onboarded 5,000+ verified companies across India" },
  { year: "2024", event: "Crossed 200,000 successful job placements" },
];

function About() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300&display=swap');

        :root {
          --blue: #2563eb; --blue-dark: #1d4ed8; --blue-light: #eff6ff;
          --navy: #0f172a; --slate: #475569; --muted: #94a3b8;
          --bg: #f8fafc; --white: #ffffff;
        }
        * { box-sizing: border-box; }
        body { font-family: 'DM Sans', sans-serif; }

        /* ── Breadcrumb ── */
        .ab-breadcrumb {
          background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%);
          padding: 48px 0 52px; position: relative; overflow: hidden;
        }
        .ab-breadcrumb::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(ellipse at 80% 50%, rgba(99,102,241,.2) 0%, transparent 60%);
        }
        .ab-breadcrumb-inner { position: relative; z-index: 1; }
        .ab-page-title {
          font-family: 'Syne', sans-serif; font-weight: 800;
          font-size: clamp(28px, 5vw, 44px); color: #fff; margin-bottom: 10px;
        }
        .ab-crumb-nav { display: flex; align-items: center; gap: 8px; }
        .ab-crumb-link { color: #93c5fd; text-decoration: none; font-size: 14px; font-weight: 500; }
        .ab-crumb-link:hover { color: #fff; }
        .ab-crumb-sep { color: #475569; font-size: 14px; }
        .ab-crumb-current { color: rgba(255,255,255,.6); font-size: 14px; }

        /* ── Shared ── */
        .ab-section { padding: 80px 0; }
        .ab-section-alt { padding: 80px 0; background: var(--bg); }
        .ab-tag {
          display: inline-block; background: var(--blue-light); color: var(--blue);
          font-size: 11px; font-weight: 700; letter-spacing: 1.2px;
          text-transform: uppercase; padding: 4px 14px; border-radius: 20px; margin-bottom: 14px;
        }
        .ab-title {
          font-family: 'Syne', sans-serif; font-weight: 800;
          font-size: clamp(22px, 3.5vw, 34px); color: var(--navy);
          line-height: 1.2; margin-bottom: 18px;
        }
        .ab-text { color: var(--slate); font-size: 15px; line-height: 1.78; margin-bottom: 14px; }

        .ab-btn {
          display: inline-block; background: var(--blue); color: #fff;
          padding: 12px 28px; border-radius: 10px; font-weight: 700;
          font-size: 14px; text-decoration: none;
          transition: background .2s, transform .15s, box-shadow .2s;
          box-shadow: 0 4px 16px rgba(37,99,235,.3);
        }
        .ab-btn:hover { background: var(--blue-dark); transform: translateY(-2px); color: #fff; box-shadow: 0 8px 24px rgba(37,99,235,.4); }

        /* ── About Main ── */
        .ab-img-wrap {
          border-radius: 20px; overflow: hidden;
          box-shadow: 0 24px 60px rgba(0,0,0,.12); position: relative;
        }
        .ab-img-wrap img { width: 100%; height: 460px; object-fit: cover; display: block; }
        .ab-img-badge {
          position: absolute; bottom: 20px; right: 20px;
          background: var(--blue); color: #fff; border-radius: 14px;
          padding: 14px 18px; text-align: center;
          box-shadow: 0 8px 28px rgba(37,99,235,.45);
        }
        .ab-img-badge-num { font-family: 'Syne', sans-serif; font-size: 26px; font-weight: 800; display: block; }
        .ab-img-badge-label { font-size: 11px; color: #bfdbfe; font-weight: 500; }

        .ab-checklist { list-style: none; padding: 0; margin: 20px 0 28px; }
        .ab-checklist li {
          display: flex; align-items: flex-start; gap: 10px;
          font-size: 14px; color: var(--slate); padding: 7px 0;
          line-height: 1.5; font-weight: 500;
        }
        .ab-checklist li::before {
          content: '✓'; min-width: 22px; height: 22px;
          background: #dcfce7; color: #16a34a;
          border-radius: 50%; display: flex; align-items: center;
          justify-content: center; font-size: 11px; font-weight: 800;
          margin-top: 1px;
        }

        /* ── Values ── */
        .ab-value-card {
          background: var(--white); border-radius: 16px; padding: 30px 26px;
          border: 1.5px solid #e8eaf0; height: 100%;
          transition: box-shadow .25s, transform .2s, border-color .2s;
          position: relative; overflow: hidden;
        }
        .ab-value-card::after {
          content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, var(--blue), #60a5fa);
          transform: scaleX(0); transition: transform .3s; transform-origin: left;
        }
        .ab-value-card:hover { box-shadow: 0 12px 36px rgba(37,99,235,.1); transform: translateY(-4px); border-color: #bfdbfe; }
        .ab-value-card:hover::after { transform: scaleX(1); }
        .ab-value-icon {
          width: 52px; height: 52px; background: var(--blue-light);
          border-radius: 14px; display: flex; align-items: center;
          justify-content: center; font-size: 24px; margin-bottom: 16px;
        }
        .ab-value-title { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 17px; color: var(--navy); margin-bottom: 8px; }
        .ab-value-desc { color: var(--slate); font-size: 14px; line-height: 1.65; }

        /* ── Timeline ── */
        .ab-timeline { position: relative; padding-left: 28px; }
        .ab-timeline::before {
          content: ''; position: absolute; left: 7px; top: 8px; bottom: 8px;
          width: 2px; background: linear-gradient(to bottom, var(--blue), #bfdbfe);
          border-radius: 2px;
        }
        .ab-timeline-item { position: relative; margin-bottom: 32px; }
        .ab-timeline-item::before {
          content: ''; position: absolute; left: -25px; top: 6px;
          width: 12px; height: 12px; border-radius: 50%;
          background: var(--blue); border: 3px solid #bfdbfe;
          box-shadow: 0 0 0 3px #eff6ff;
        }
        .ab-timeline-year {
          font-family: 'Syne', sans-serif; font-weight: 800;
          font-size: 13px; color: var(--blue); text-transform: uppercase;
          letter-spacing: 1px; margin-bottom: 4px;
        }
        .ab-timeline-text { font-size: 14px; color: var(--slate); line-height: 1.6; }

        /* ── Team ── */
        .ab-team-card {
          background: var(--white); border-radius: 16px; padding: 28px 20px;
          text-align: center; border: 1.5px solid #e8eaf0;
          transition: box-shadow .22s, transform .2s;
        }
        .ab-team-card:hover { box-shadow: 0 10px 32px rgba(37,99,235,.1); transform: translateY(-3px); }
        .ab-team-avatar {
          width: 70px; height: 70px; border-radius: 50%;
          background: linear-gradient(135deg, #dbeafe, #eff6ff);
          display: flex; align-items: center; justify-content: center;
          font-size: 32px; margin: 0 auto 14px;
          border: 3px solid #bfdbfe;
        }
        .ab-team-name { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 15px; color: var(--navy); margin-bottom: 4px; }
        .ab-team-role { font-size: 13px; color: var(--blue); font-weight: 600; }

        /* ── CTA ── */
        .ab-cta {
          background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1d4ed8 100%);
          padding: 72px 0; text-align: center; position: relative; overflow: hidden;
        }
        .ab-cta::before {
          content: ''; position: absolute; width: 400px; height: 400px;
          border-radius: 50%; top: -120px; right: -80px;
          background: radial-gradient(circle, rgba(99,102,241,.18) 0%, transparent 70%);
        }
        .ab-cta-inner { position: relative; z-index: 1; }
        .ab-cta-title { font-family: 'Syne', sans-serif; font-weight: 800; font-size: clamp(24px, 4vw, 38px); color: #fff; margin-bottom: 12px; }
        .ab-cta-sub { color: #93c5fd; font-size: 15px; margin-bottom: 28px; max-width: 440px; margin-left: auto; margin-right: auto; }
        .ab-btn-outline {
          display: inline-block; background: rgba(255,255,255,.12);
          color: #fff; padding: 12px 28px; border-radius: 10px;
          font-weight: 600; font-size: 14px; text-decoration: none;
          border: 1.5px solid rgba(255,255,255,.3); margin-left: 10px;
          transition: background .2s;
        }
        .ab-btn-outline:hover { background: rgba(255,255,255,.22); color: #fff; }

        @media (max-width: 768px) {
          .ab-section, .ab-section-alt { padding: 50px 0; }
          .ab-img-wrap { margin-bottom: 32px; }
        }
      `}</style>

      <WebHeader />

      {/* ── Breadcrumb Hero ── */}
      <div className="ab-breadcrumb">
        <div className="container ab-breadcrumb-inner">
          <h1 className="ab-page-title">About Us</h1>
          <nav className="ab-crumb-nav">
            <Link to="/" className="ab-crumb-link">Home</Link>
            <span className="ab-crumb-sep">›</span>
            <span className="ab-crumb-current">About Us</span>
          </nav>
        </div>
      </div>

      {/* ── Main About ── */}
      <section className="ab-section">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div className="ab-img-wrap">
                <img src="assets/images/auth/about1.webp" alt="About Us" />
                <div className="ab-img-badge">
                  <span className="ab-img-badge-num">200K+</span>
                  <span className="ab-img-badge-label">Placements Made</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="ab-tag">About Us</div>
              <h2 className="ab-title">Recruitment Reimagined with AI Intelligence</h2>
              <p className="ab-text">
                <strong>AI Powered Shortlisted Jobs</strong> is an intelligent recruitment platform built to transform how talent connects with opportunity. Using advanced AI, data analytics, and automation, every job recommendation is relevant, personalized, and meaningful.
              </p>
              <p className="ab-text">
                Our platform evaluates candidates beyond keywords — analyzing skills, experience, qualifications, and career goals to shortlist jobs that truly match potential. For employers, our AI-driven screening reduces effort, removes unconscious bias, and delivers high-quality talent faster.
              </p>
              <ul className="ab-checklist">
                <li>AI-driven skill matching across thousands of live roles</li>
                <li>Unbiased screening for fair candidate evaluation</li>
                <li>Real-time application tracking and status updates</li>
                <li>Verified companies and transparent hiring process</li>
                <li>Continuously learning AI for better matches over time</li>
              </ul>
              <Link to="/jobs" className="ab-btn">Explore Jobs →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Our Values ── */}
      <section className="ab-section-alt">
        <div className="container">
          <div className="text-center mb-5">
            <div className="ab-tag">Our Values</div>
            <h2 className="ab-title">What Drives Everything We Do</h2>
          </div>
          <div className="row g-4">
            {values.map((v) => (
              <div key={v.title} className="col-sm-6 col-lg-3">
                <div className="ab-value-card">
                  <div className="ab-value-icon">{v.icon}</div>
                  <div className="ab-value-title">{v.title}</div>
                  <p className="ab-value-desc">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Journey + Team ── */}
      <section className="ab-section">
        <div className="container">
          <div className="row g-5">

            {/* Timeline */}
            <div className="col-lg-6">
              <div className="ab-tag">Our Journey</div>
              <h2 className="ab-title">How We Got Here</h2>
              <p className="ab-text" style={{ marginBottom: 32 }}>
                From a small idea to a platform trusted by hundreds of thousands — here are the milestones that shaped us.
              </p>
              <div className="ab-timeline">
                {milestones.map((m) => (
                  <div key={m.year} className="ab-timeline-item">
                    <div className="ab-timeline-year">{m.year}</div>
                    <div className="ab-timeline-text">{m.event}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Team */}
            <div className="col-lg-6">
              <div className="ab-tag">Our Team</div>
              <h2 className="ab-title">The People Behind the Platform</h2>
              <p className="ab-text" style={{ marginBottom: 32 }}>
                A passionate team of technologists, recruiters, and product thinkers united by one goal — making hiring better for everyone.
              </p>
              <div className="row g-3">
                {team.map((t) => (
                  <div key={t.name} className="col-6">
                    <div className="ab-team-card">
                      <div className="ab-team-avatar">{t.emoji}</div>
                      <div className="ab-team-name">{t.name}</div>
                      <div className="ab-team-role">{t.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ab-cta">
        <div className="container ab-cta-inner">
          <h2 className="ab-cta-title">Ready to Join 200,000+ Professionals?</h2>
          <p className="ab-cta-sub">Start your AI-powered job search today and find roles that truly match your potential.</p>
          <div>
            <Link to="/jobs" className="ab-btn">Browse Jobs →</Link>
            <Link to="/" className="ab-btn-outline">Back to Home</Link>
          </div>
        </div>
      </section>

      <WebFooter />
    </>
  );
}

export default About;
